// Helpers compartilhados entre a listagem, o drawer e o preview.
// As validacoes espelham as do backend (messengerTemplates.controller.js) de proposito:
// um template rejeitado pela Meta consome cota de revisao e chega ao usuario como um
// erro opaco horas depois — vale barrar antes de sair da tela.

export const PLACEHOLDER_REGEX = /\{\{\s*([^{}\s]+)\s*\}\}/g;

export const extrairPlaceholders = (texto) => {
    if (!texto) return [];
    return [...String(texto).matchAll(PLACEHOLDER_REGEX)].map((m) => m[1]);
};

// Placeholders únicos preservando a ordem de aparição
export const extrairPlaceholdersUnicos = (texto) => [...new Set(extrairPlaceholders(texto))];

export const slugificar = (nome) => String(nome || '')
    .toLowerCase()
    .trim()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9\s_]/g, '')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');

export const IDIOMAS = [
    { value: 'pt_BR', label: 'Português (Brasil)' },
    { value: 'pt_PT', label: 'Português (Portugal)' },
    { value: 'en', label: 'Inglês' },
    { value: 'en_US', label: 'Inglês (EUA)' },
    { value: 'es', label: 'Espanhol' },
    { value: 'es_ES', label: 'Espanhol (Espanha)' }
];

export const statusBadgeClasses = (status) => {
    switch (status) {
        case 'APPROVED': return 'bg-green-50 text-green-700 border-green-200';
        case 'REJECTED': return 'bg-red-50 text-red-700 border-red-200';
        case 'PENDING': return 'bg-amber-50 text-amber-700 border-amber-200';
        default: return 'bg-gray-50 text-gray-700 border-gray-200';
    }
};

export const statusDotClasses = (status) => {
    switch (status) {
        case 'APPROVED': return 'bg-green-500';
        case 'REJECTED': return 'bg-red-500';
        case 'PENDING': return 'bg-amber-500';
        default: return 'bg-gray-400';
    }
};

/**
 * Valida o formulário e devolve a lista de problemas (vazia = ok).
 *
 * @param {object} form
 * @param {string} form.templateName
 * @param {'POSITIONAL'|'NAMED'} form.parameterFormat
 * @param {'none'|'TEXT'|'IMAGE'} form.headerType
 * @param {string} form.headerText
 * @param {string} form.headerHandle
 * @param {string} form.body
 * @param {Array} form.buttons  [{ type, text, url, payload, exemploUrl }]
 * @param {object} form.variaveis  { [chave]: valorDeExemplo } — chave = nome ou posição
 */
export const validarTemplate = (form) => {
    const erros = [];
    const isNamed = form.parameterFormat === 'NAMED';

    if (!form.templateName?.trim()) erros.push('Informe o nome do template');
    else if (!slugificar(form.templateName)) erros.push('O nome precisa ter letras ou números');

    const corpo = String(form.body || '').trim();
    if (!corpo) erros.push('O conteúdo da mensagem é obrigatório');

    const validarTexto = (texto, rotulo, exigirSequencia) => {
        const bruto = String(texto || '');
        const abre = (bruto.match(/\{\{/g) || []).length;
        const fecha = (bruto.match(/\}\}/g) || []).length;
        if (abre !== fecha) erros.push(`${rotulo}: chaves de variável desbalanceadas — use {{assim}}`);

        const placeholders = extrairPlaceholders(bruto);

        for (const ph of placeholders) {
            if (isNamed) {
                if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(ph)) {
                    erros.push(`${rotulo}: "{{${ph}}}" inválida — no formato nomeado comece com letra e use apenas letras, números e _`);
                }
            } else if (!/^\d+$/.test(ph)) {
                erros.push(`${rotulo}: "{{${ph}}}" inválida — no formato posicional use números, como {{1}}`);
            }
            if (/[#$%]/.test(ph)) erros.push(`${rotulo}: "{{${ph}}}" não pode conter # $ %`);
        }

        // Sequência posicional: {{1}},{{2}},{{4}} é rejeitado pela Meta
        if (exigirSequencia && !isNamed) {
            const numeros = [...new Set(placeholders.map(Number).filter(Number.isInteger))].sort((a, b) => a - b);
            for (let i = 0; i < numeros.length; i++) {
                if (numeros[i] !== i + 1) {
                    erros.push(`${rotulo}: as variáveis devem ser sequenciais a partir de {{1}} — falta {{${i + 1}}}`);
                    break;
                }
            }
        }

        return placeholders;
    };

    // "Dangling parameter": a Meta não aceita corpo que começa ou termina com variável
    if (corpo) {
        if (/^\{\{[^{}]+\}\}/.test(corpo)) erros.push('A mensagem não pode começar com uma variável');
        if (/\{\{[^{}]+\}\}$/.test(corpo)) erros.push('A mensagem não pode terminar com uma variável');
    }

    const phBody = validarTexto(form.body, 'Mensagem', true);

    // Razão variáveis/palavras (PARAMS_TO_WORD_RATIO_EXCEED_LIMIT)
    const palavras = corpo.replace(PLACEHOLDER_REGEX, '').split(/\s+/).filter(Boolean).length;
    if (phBody.length && palavras < phBody.length * 3) {
        erros.push('Muitas variáveis para o tamanho da mensagem — escreva mais texto ou use menos variáveis');
    }

    let phHeader = [];
    if (form.headerType === 'TEXT') {
        if (!String(form.headerText || '').trim()) erros.push('O título está vazio — remova o título ou preencha');
        phHeader = validarTexto(form.headerText, 'Título', true);
    }
    if (form.headerType === 'IMAGE' && !form.headerHandle) {
        erros.push('Envie a imagem do título antes de salvar');
    }

    // Todo placeholder precisa de valor de exemplo — é o que a Meta usa na revisão
    for (const ph of [...new Set([...phHeader, ...phBody])]) {
        const valor = form.variaveis?.[ph];
        if (valor === undefined || valor === null || String(valor).trim() === '') {
            erros.push(`Informe um valor de exemplo para "{{${ph}}}"`);
        }
    }

    const buttons = form.buttons || [];
    if (buttons.length > 3) erros.push('A Meta aceita no máximo 3 botões');
    buttons.forEach((btn, i) => {
        const rotulo = `Botão ${i + 1}`;
        if (!btn.text?.trim()) erros.push(`${rotulo}: informe o texto`);
        if (btn.type === 'URL') {
            if (!btn.url?.trim()) erros.push(`${rotulo}: informe a URL`);
            if (btn.url && extrairPlaceholders(btn.url).length && !btn.exemploUrl?.trim()) {
                erros.push(`${rotulo}: informe um exemplo da URL completa`);
            }
        }
        if (btn.type === 'POSTBACK' && !btn.payload?.trim()) {
            erros.push(`${rotulo}: informe o payload`);
        }
    });

    return [...new Set(erros)];
};

/**
 * Converte o estado do formulário nos `components` do formato da Meta,
 * já com os `example` no formato correspondente ao parameter_format.
 */
export const montarComponents = (form) => {
    const isNamed = form.parameterFormat === 'NAMED';
    const components = [];

    const exemploDe = (ph) => String(form.variaveis?.[ph] ?? '');

    // ---- HEADER
    if (form.headerType === 'TEXT' && String(form.headerText || '').trim()) {
        const placeholders = extrairPlaceholdersUnicos(form.headerText);
        const header = { type: 'HEADER', format: 'TEXT', text: form.headerText };

        if (placeholders.length) {
            header.example = isNamed
                ? { header_text_named_params: placeholders.map((ph) => ({ param_name: ph, example: exemploDe(ph) })) }
                // Posicional: header_text é um array simples, ordenado pela posição
                : { header_text: placeholders.slice().sort((a, b) => Number(a) - Number(b)).map(exemploDe) };
        }
        components.push(header);
    }

    if (form.headerType === 'IMAGE' && form.headerHandle) {
        components.push({
            type: 'HEADER',
            format: 'IMAGE',
            example: { header_handle: [form.headerHandle] },
            // Campos internos: o backend remove antes de enviar à Meta e mantém no Mongo
            // para o preview não depender da CDN da Meta (que expira).
            mediaUrl: form.headerMediaUrl,
            mediaFilename: form.headerMediaFilename
        });
    }

    // ---- BODY
    const placeholdersBody = extrairPlaceholdersUnicos(form.body);
    const body = { type: 'BODY', text: form.body };
    if (placeholdersBody.length) {
        body.example = isNamed
            ? { body_text_named_params: placeholdersBody.map((ph) => ({ param_name: ph, example: exemploDe(ph) })) }
            // Posicional: body_text é um array de arrays (um array por exemplo)
            : { body_text: [placeholdersBody.slice().sort((a, b) => Number(a) - Number(b)).map(exemploDe)] };
    }
    components.push(body);

    // ---- BUTTONS
    if (form.buttons?.length) {
        components.push({
            type: 'BUTTONS',
            buttons: form.buttons.map((btn) => {
                if (btn.type === 'URL') {
                    const b = { type: 'URL', text: btn.text, url: btn.url };
                    if (extrairPlaceholders(btn.url).length) {
                        b.example = { url_suffix_example: btn.exemploUrl };
                    }
                    return b;
                }
                return { type: 'POSTBACK', text: btn.text, payload: btn.payload };
            })
        });
    }

    return components;
};

// ---- leitura de um template salvo (para visualizar / preencher o envio de teste)

export const componentePorTipo = (components, tipo) =>
    (components || []).find((c) => String(c?.type).toUpperCase() === tipo);

export const textoDoBody = (components) => componentePorTipo(components, 'BODY')?.text || '';

export const botoesDoTemplate = (components) => componentePorTipo(components, 'BUTTONS')?.buttons || [];

/**
 * Variáveis de um template salvo, separadas por componente — é o que o envio de teste
 * precisa preencher (a Meta exige um parâmetro por placeholder, na ordem certa).
 */
export const variaveisDoTemplate = (template) => {
    const components = template?.components || [];
    const header = componentePorTipo(components, 'HEADER');
    const body = componentePorTipo(components, 'BODY');
    const isNamed = template?.parameter_format === 'NAMED';

    const ordenar = (lista) => (isNamed ? lista : lista.slice().sort((a, b) => Number(a) - Number(b)));

    return {
        isNamed,
        header: ordenar(extrairPlaceholdersUnicos(header?.text)),
        body: ordenar(extrairPlaceholdersUnicos(body?.text)),
        buttons: botoesDoTemplate(components)
            .map((btn, index) => {
                const tipo = String(btn.type).toUpperCase();
                if (tipo === 'URL' && extrairPlaceholders(btn.url).length) {
                    return { index, type: 'URL', text: btn.text, exemplo: btn.example?.url_suffix_example };
                }
                if (tipo === 'POSTBACK' && extrairPlaceholders(btn.payload).length) {
                    return { index, type: 'POSTBACK', text: btn.text };
                }
                return null;
            })
            .filter(Boolean)
    };
};
